import { makeAutoObservable } from "mobx";
import axios from "axios";
import { format } from "date-fns";
import { getAPIUrl } from "../../api/apiUtils";
import { groupIdToTitle } from "../../utils/groupTitles";
import { apiFetch } from "../../api/apiClient";

const getVkPostLink = (groupId, postId) => {
  if (!groupId || !postId) return null;
  const numericGroupId = Number(groupId);
  if (Number.isNaN(numericGroupId)) return null;
  const vkGroupId = numericGroupId > 0 ? -numericGroupId : numericGroupId;
  return `https://vk.com/wall${vkGroupId}_${postId}`;
};

const formatPostDate = (post) => {
  if (post.formatted_date) return post.formatted_date;
  if (!post.date) return "-";
  const timestamp =
    typeof post.date === "number" ? post.date * 1000 : Number(post.date);
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "yyyy-MM-dd HH:mm:ss");
};

const buildPostModalRow = (post) => {
  if (!post) return null;
  const rawGroupId = post.group_id ?? post.from_id;
  const groupId = rawGroupId != null ? String(rawGroupId) : "-";
  const postId = post.id ?? post.post_id ?? "-";

  const row = {
    "Дата": formatPostDate(post),
    // "group_id": groupId,
    "Название сообщества": groupIdToTitle[groupId] || groupId,
    "ID поста": postId,
    "Текст": post.text ?? "-",
    "Комментарии": post.comments_count ?? "-",
    "Лайки": post.likes?.count ?? "-",
    "Просмотры": post.views?.count ?? "-",
    "Ссылка на пост": getVkPostLink(groupId, postId) ?? "-",
  };

  return row;
};

class CombinedChartStore {
  data = [];
  loading = false;
  error = null;
  cache = {};

  constructor() {
    makeAutoObservable(this);
  }

  getCacheKey(filters) {
    return JSON.stringify(filters);
  }

  async fetchData(filters = null) {
    this.loading = true;
    this.error = null;
    try {
      let currentFilters = filters;
      if (!currentFilters) {
        const saved = localStorage.getItem('globalFilters');
        currentFilters = saved ? JSON.parse(saved) : {};
      }

      const cacheKey = this.getCacheKey(currentFilters);
      if (this.cache[cacheKey]) {
        this.data = this.cache[cacheKey];
        this.loading = false;
        return;
      }

      if (!currentFilters.selectedGroups || currentFilters.selectedGroups.length === 0) {
        this.data = [];
        this.loading = false;
        return;
      }

      const params = new URLSearchParams();
      if (currentFilters.selectedGroups) {
        params.append('group_ids', currentFilters.selectedGroups.join(','));
      }
      if (currentFilters.startDate) {
        params.append('start_date', currentFilters.startDate);
      }
      if (currentFilters.endDate) {
        params.append('end_date', currentFilters.endDate);
      }

      const url = `${getAPIUrl()}/assessment/graph_quarters${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiFetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      this.data = result;
      this.cache[cacheKey] = result;
    } catch (e) {
      this.error = e;
    } finally {
      this.loading = false;
    }
  }

  async fetchAllData(filters = null) {
    let currentFilters = filters;
    if (!currentFilters) {
      const saved = localStorage.getItem('globalFilters');
      currentFilters = saved ? JSON.parse(saved) : {};
    } else if (typeof currentFilters === 'string') {
      currentFilters = JSON.parse(currentFilters);
    }

    const cached = this.cache[this.getCacheKey(currentFilters)];
    if (cached) return cached;

    await this.fetchData(currentFilters);
    if (this.error) throw this.error;
    return this.data;
  }

  /**
   * Получает посты по выбранным группам и диапазону дат в формате,
   * подходящем для ChartPointDataModal (rows).
   */
  async fetchPostsForModal({
    groupIds,
    startDate,
    endDate,
    limit = 10000,
    page = 1,
    maxPages = 20,
  } = {}) {
    console.log("groupIds: ",groupIds, "\nStartDate: ", startDate,"\nendDate: ",endDate)
    try {
      let effectiveGroups = groupIds;
      let effectiveStart = startDate;
      let effectiveEnd = endDate;

      if (
        (!effectiveGroups || !effectiveGroups.length) ||
        !effectiveStart ||
        !effectiveEnd
      ) {
        const savedFilters = localStorage.getItem("globalFilters");
        if (savedFilters) {
          try {
            const parsed = JSON.parse(savedFilters);
            if (!effectiveGroups || !effectiveGroups.length) {
              effectiveGroups = parsed?.selectedGroups || [];
            }
            if (!effectiveStart) {
              effectiveStart = parsed?.startDate;
            }
            if (!effectiveEnd) {
              effectiveEnd = parsed?.endDate;
            }
          } catch (err) {
            console.warn("Не удалось распарсить globalFilters:", err);
          }
        }
      }

      if (
        !effectiveGroups ||
        !effectiveGroups.length ||
        !effectiveStart ||
        !effectiveEnd
      ) {
        return [];
      }

      const rows = [];
      let currentPage = page;
      let remaining = null;

      for (let requestIndex = 0; requestIndex < maxPages; requestIndex++) {
        const params = {
          group_ids: effectiveGroups.join(","),
          page: currentPage,
          limit,
          startDate: effectiveStart,
          endDate: effectiveEnd,
        };

        const response = await axios.get(
          `${getAPIUrl()}/vk/collection/multiple`,
          { params }
        );
        console.log("result: ",response.data)
        const items = Array.isArray(response.data)
          ? response.data
          : response.data?.items || [];

        if (remaining == null) {
          const total = response.data?.count;
          if (typeof total === "number") {
            remaining = Math.max(total, 0);
          }
        }

        const pageRows = items
          .map((item) => buildPostModalRow(item))
          .filter((row) => row !== null);

        rows.push(...pageRows);

        if (remaining != null) {
          remaining -= items.length;
          if (remaining <= 0) {
            break;
          }
        }

        if (items.length < limit) {
          break;
        }

        currentPage += 1;
      }

      return rows.sort((a, b) => {
        const dateA = new Date(a["Дата"]);
        const dateB = new Date(b["Дата"]);
        return dateA - dateB;
      });
    } catch (error) {
      console.error("Ошибка при получении постов для модального окна:", error);
      return [];
    }
  }
}

export const combinedChartStore = new CombinedChartStore();
